import { useState, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import { X } from "lucide-react";
import { Container, Reveal, PageBanner, siteImg } from "@/site/ui";

export default function Photos() {
  const c = useOutletContext();
  const p = c.photos || {};
  const items = p.items || [];
  const albums = useMemo(() => ["All", ...Array.from(new Set(items.map((i) => i.album).filter(Boolean)))], [items]);
  const [album, setAlbum] = useState("All");
  const [lightbox, setLightbox] = useState(null);
  const shown = album === "All" ? items : items.filter((i) => i.album === album);

  return (
    <div data-testid="photos-page">
      <PageBanner image={c.banners?.photos} eyebrow="Photos" title={p.heading || "Photo gallery"} subtitle={p.subtitle} />

      <section className="py-16 md:py-24">
        <Container>
          {albums.length > 2 && (
            <div className="mb-10 flex flex-wrap gap-2" data-testid="photo-filters">
              {albums.map((a) => (
                <button key={a} onClick={() => setAlbum(a)} data-testid={`photo-filter-${a.toLowerCase()}`}
                  className={`rounded-full px-5 py-2 text-sm font-medium transition-colors duration-200 ${album === a ? "bg-[var(--rgh-primary)] text-white" : "bg-gray-100 text-[#525252] hover:bg-gray-200"}`}>
                  {a}
                </button>
              ))}
            </div>
          )}

          {shown.length === 0 ? (
            <div className="h-40 flex items-center justify-center rounded-2xl border border-dashed border-gray-200 text-[#A3A3A3]">No photos yet.</div>
          ) : (
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [&>*]:mb-5">
              {shown.map((it, i) => (
                <Reveal key={it.id || i} delay={(i % 3) * 0.06}>
                  <button onClick={() => setLightbox(it)} data-testid={`photo-${i}`}
                    className="group relative block w-full overflow-hidden rounded-2xl border border-gray-100">
                    <img src={siteImg(it.image)} alt={it.title} className="w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    {(it.title || it.album) && (
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-4 opacity-0 transition-opacity duration-300 group-hover:opacity-100 text-left">
                        {it.title && <p className="text-sm font-semibold text-white">{it.title}</p>}
                        {it.album && <p className="text-xs text-white/70">{it.album}</p>}
                      </div>
                    )}
                  </button>
                </Reveal>
              ))}
            </div>
          )}
        </Container>
      </section>

      {lightbox && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4" onClick={() => setLightbox(null)} data-testid="photo-lightbox">
          <button className="absolute top-6 right-6 text-white/80 hover:text-white" onClick={() => setLightbox(null)}><X size={28} /></button>
          <img src={siteImg(lightbox.image)} alt={lightbox.title} className="max-h-[85vh] max-w-full rounded-xl object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
